import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as ChevronLeftIcon } from 'assets/icons/chevron-left.svg'
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow_short_right.svg'
import { ROUTES } from 'shared/config/routes'
import { chatWidgetStore } from 'shared/stores/chatWidgetStore'
import curriculumImage from 'assets/images/feature-carousel/curriculum.webp'
import aiLorWriterImage from 'assets/images/feature-carousel/ai-lor-writer-v2.webp'
import noteTakerImage from 'assets/images/feature-carousel/note-taker.webp'
import essayFeedbackImage from 'assets/images/feature-carousel/essay-feedback-v2.webp'
import kodaAgenticAiImage from 'assets/images/feature-carousel/koda-agentic-ai-v7.webp'
import { getNextFeatureStartIndex } from './FeatureDiscoveryCarousel.utils'
import s from './FeatureDiscoveryCarousel.module.scss'

const LAST_SEEN_INDEX_KEY = 'kandor:counsellor-feature-carousel:last-seen-index'
const AUTOPLAY_INTERVAL_MS = 3500

const FEATURES = [
	{
		id: 'curriculum',
		title: 'Curriculum',
		description:
			'Build structured plans, assign activities, and keep every learner moving forward.',
		cta: 'Open curriculum',
		to: ROUTES.CLASSROOM,
		image: curriculumImage,
		theme: 'violet',
		tag: 'Guided learning',
	},
	{
		id: 'lor-writer',
		title: 'AI LoR Writer',
		description:
			'Create thoughtful, student-specific recommendation letters with AI-assisted drafting.',
		cta: 'Create a LoR',
		to: ROUTES.APPLICATIONS,
		image: aiLorWriterImage,
		imageSize: 'smallImage',
		theme: 'coral',
		tag: 'AI writing',
	},
	{
		id: 'note-taker',
		title: 'Note Taker',
		description:
			'After each session, record a quick student brain dump. Koda turns it into clear notes and next steps.',
		cta: 'Start note taking',
		action: 'note-taker',
		image: noteTakerImage,
		theme: 'blue',
		tag: 'Session notes',
	},
	{
		id: 'essay-feedback',
		title: 'Essay Feedback',
		description:
			'Review student essays, share focused feedback, and keep every revision moving.',
		cta: 'Review essays',
		to: ROUTES.APPLICATIONS,
		image: essayFeedbackImage,
		imageSize: 'smallImage',
		theme: 'green',
		tag: 'Essay review',
	},
	{
		id: 'koda-agentic-ai',
		title: 'Koda Agentic AI',
		description:
			'Tell Koda what you need done and let it take action across the platform for you.',
		cta: 'Ask Koda',
		action: 'koda',
		image: kodaAgenticAiImage,
		imageSize: 'kodaImage',
		theme: 'amber',
		tag: 'Do what you speak',
	},
]

const prefersReducedMotion = () =>
	window.matchMedia('(prefers-reduced-motion: reduce)').matches

const getRotatingFeatureStartIndex = () => {
	if (typeof window === 'undefined') return 0

	try {
		return getNextFeatureStartIndex(
			window.localStorage.getItem(LAST_SEEN_INDEX_KEY),
			FEATURES.length
		)
	} catch {
		return 0
	}
}

const FeatureDiscoveryCarousel = () => {
	const navigate = useNavigate()
	const [startingFeatureIndex] = useState(getRotatingFeatureStartIndex)
	const [slideIndex, setSlideIndex] = useState(startingFeatureIndex + 1)
	const [shouldAnimate, setShouldAnimate] = useState(true)
	const [isSliding, setIsSliding] = useState(false)
	const [isPaused, setIsPaused] = useState(false)
	const activeIndex = (slideIndex - 1 + FEATURES.length) % FEATURES.length
	const slides = [FEATURES[FEATURES.length - 1], ...FEATURES, FEATURES[0]]
	const trackTransform = `translate3d(calc(-${slideIndex * 100}% - ${
		slideIndex * 8
	}px), 0, 0)`

	useEffect(() => {
		try {
			window.localStorage.setItem(
				LAST_SEEN_INDEX_KEY,
				String(activeIndex)
			)
		} catch {
			// The carousel still works when browser storage is unavailable.
		}
	}, [activeIndex])

	useEffect(() => {
		if (
			isPaused ||
			isSliding ||
			prefersReducedMotion()
		) {
			return undefined
		}

		const intervalId = window.setInterval(() => {
			setShouldAnimate(true)
			setIsSliding(true)
			setSlideIndex(current => current + 1)
		}, AUTOPLAY_INTERVAL_MS)

		return () => window.clearInterval(intervalId)
	}, [isPaused, isSliding])

	const showPrevious = () => {
		if (isSliding) return
		if (prefersReducedMotion()) {
			setShouldAnimate(false)
			setSlideIndex(current => (current === 1 ? FEATURES.length : current - 1))
			return
		}
		setShouldAnimate(true)
		setIsSliding(true)
		setSlideIndex(current => current - 1)
	}

	const showNext = () => {
		if (isSliding) return
		if (prefersReducedMotion()) {
			setShouldAnimate(false)
			setSlideIndex(current => (current === FEATURES.length ? 1 : current + 1))
			return
		}
		setShouldAnimate(true)
		setIsSliding(true)
		setSlideIndex(current => current + 1)
	}

	const showFeature = index => {
		if (isSliding || index === activeIndex) return
		if (prefersReducedMotion()) {
			setShouldAnimate(false)
			setSlideIndex(index + 1)
			return
		}
		setShouldAnimate(true)
		setIsSliding(true)
		setSlideIndex(index + 1)
	}

	const handleTrackTransitionEnd = event => {
		if (event.target !== event.currentTarget || event.propertyName !== 'transform') return

		if (slideIndex === 0) {
			setShouldAnimate(false)
			setSlideIndex(FEATURES.length)
			setIsSliding(false)
			return
		}

		if (slideIndex === FEATURES.length + 1) {
			setShouldAnimate(false)
			setSlideIndex(1)
			setIsSliding(false)
			return
		}

		setIsSliding(false)
	}

	const handleFeatureAction = action => {
		if (action === 'koda') {
			chatWidgetStore.open()
			return
		}

		if (action === 'note-taker') {
			document.querySelector('[data-note-taker-trigger]')?.click()
		}
	}

	const activateFeature = selectedFeature => {
		if (selectedFeature.to) {
			navigate(selectedFeature.to)
			return
		}

		handleFeatureAction(selectedFeature.action)
	}

	const handleCardKeyDown = (event, selectedFeature) => {
		if (event.key !== 'Enter' && event.key !== ' ') return
		event.preventDefault()
		activateFeature(selectedFeature)
	}

	return (
		<section
			className={s.carousel}
			aria-roledescription="carousel"
			aria-label="Discover Kandor features"
			onMouseEnter={() => setIsPaused(true)}
			onMouseLeave={() => setIsPaused(false)}
			onFocusCapture={() => setIsPaused(true)}
			onBlurCapture={() => setIsPaused(false)}
		>
			<div className={s.stage}>
				<div className={s.ambientShell} aria-hidden="true">
					<div className={s.ambientViewport}>
						<div
							className={`${s.ambientTrack} ${
								shouldAnimate ? '' : s.trackWithoutTransition
							}`}
							style={{ transform: trackTransform }}
						>
							{slides.map((item, itemIndex) => (
								<div
									key={`${item.id}-ambient-${itemIndex}`}
									className={`${s.ambientSlide} ${s[item.theme]}`}
								/>
							))}
						</div>
					</div>
				</div>

				<div className={s.viewport}>
					<div
						className={`${s.track} ${shouldAnimate ? '' : s.trackWithoutTransition}`}
						style={{ transform: trackTransform }}
						onTransitionEnd={handleTrackTransitionEnd}
					>
						{slides.map((item, itemIndex) => {
							const isCurrentSlide = itemIndex === slideIndex

							return (
								<article
									key={`${item.id}-${itemIndex}`}
									className={`${s.card} ${s[item.theme]}`}
									aria-label={`${item.title}. ${item.cta}`}
									aria-hidden={!isCurrentSlide}
									role={item.to ? 'link' : 'button'}
									tabIndex={isCurrentSlide ? 0 : -1}
									onClick={() => activateFeature(item)}
									onKeyDown={event => handleCardKeyDown(event, item)}
								>
									<div className={s.decorOne} />
									<div className={s.decorTwo} />
									<img
										className={`${s.featureImage} ${
											item.imageSize ? s[item.imageSize] : ''
										}`}
										src={item.image}
										alt=""
										aria-hidden="true"
									/>
									<div className={s.cardContent}>
										<div className={s.copy}>
											<span className={s.tag}>{item.tag}</span>
											<h3>{item.title}</h3>
											<p>{item.description}</p>
											<span className={s.cta} aria-hidden="true">
												{item.cta}
												<ArrowRightIcon />
											</span>
										</div>
									</div>
								</article>
							)
						})}
					</div>
				</div>

				<button
					type="button"
					className={`${s.arrow} ${s.arrowLeft}`}
					onClick={showPrevious}
					disabled={isSliding}
					aria-label="Show previous feature"
				>
					<ChevronLeftIcon />
				</button>
				<button
					type="button"
					className={`${s.arrow} ${s.arrowRight}`}
					onClick={showNext}
					disabled={isSliding}
					aria-label="Show next feature"
				>
					<ChevronLeftIcon />
				</button>

				<div className={s.pagination}>
					{FEATURES.map((item, index) => (
						<button
							key={item.id}
							type="button"
							className={`${s.dot} ${index === activeIndex ? s.dotActive : ''}`}
							onClick={() => showFeature(index)}
							disabled={isSliding}
							aria-label={`Show ${item.tag}`}
							aria-current={index === activeIndex ? 'true' : undefined}
						/>
					))}
				</div>
			</div>
		</section>
	)
}

export default FeatureDiscoveryCarousel
